// covered-by: frontends/carrie/src/ask-v2/home/__tests__/ActionStack.test.tsx
import {
  type AskPunttActionCard,
  type AskPunttActionResolution,
} from "@mg/schemas/src/christo/askPunttBriefing";
import { Text } from "@radix-ui/themes";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ActionCard } from "./ActionCard";
import { DigDeeperPanel } from "./DigDeeperPanel";

type ActionStackProps = {
  actions: AskPunttActionCard[];
  resolvingId: string | null;
  digDeeper: AskPunttActionCard | null;
  onResolve: (
    card: AskPunttActionCard,
    resolution: AskPunttActionResolution,
  ) => void;
  onExpandDigDeeper: () => void;
};

export function ActionStack({
  actions,
  resolvingId,
  digDeeper,
  onResolve,
  onExpandDigDeeper,
}: ActionStackProps) {
  const { t } = useTranslation("ui");
  const [expandedId, setExpandedId] = useState<string | null>(
    actions[0]?.id ?? null,
  );

  useEffect(() => {
    if (actions.length === 0) {
      setExpandedId(null);
      return;
    }
    if (!actions.some((card) => card.id === expandedId)) {
      setExpandedId(actions[0]!.id);
    }
  }, [actions, expandedId]);

  if (actions.length === 0 && !digDeeper) {
    return (
      <Text as="p" size="2" color="gray" data-testid="ask-v2-empty-stack">
        {t("askV2.home.emptyStack")}
      </Text>
    );
  }

  return (
    <div
      data-testid="ask-v2-action-stack"
      className="flex w-full max-w-2xl flex-col gap-3"
    >
      {actions.map((card) => (
        <ActionCard
          key={card.id}
          card={card}
          expanded={card.id === expandedId}
          resolving={resolvingId === card.id}
          onExpand={() => setExpandedId(card.id)}
          onResolve={(resolution) => onResolve(card, resolution)}
        />
      ))}
      {digDeeper ? (
        <DigDeeperPanel card={digDeeper} onExpand={onExpandDigDeeper} />
      ) : null}
    </div>
  );
}
